import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { calculateScores } from '../lib/scores'

const AREA_META = {
  health:        { label: 'Health',        color: '#4caf7d', icon: '💪' },
  mind:          { label: 'Mind',          color: '#9b5ce0', icon: '🧠' },
  career:        { label: 'Career',        color: '#c9a84c', icon: '📈' },
  relationships: { label: 'Relationships', color: '#e05c5c', icon: '❤️' },
  faith:         { label: 'Faith',         color: '#5c8fe0', icon: '✝️' },
  purpose:       { label: 'Goals',         color: '#e07d3c', icon: '🎯' },
}

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

export default function InsightsPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [scores, setScores] = useState(null)

  useEffect(() => {
    if (user) loadScores()
  }, [user])

  async function loadScores() {
    const { data } = await supabase
      .from('manual_tasks')
      .select('area, final_points')
      .eq('user_id', user.id)
      .eq('date', getLocalDate())
      .eq('flagged', false)

    if (data) {
      const pts = {}
      data.forEach(t => { pts[t.area] = (pts[t.area] || 0) + t.final_points })
      setScores(calculateScores({}, {}, pts, profile?.streak || 0))
    } else {
      setScores(calculateScores({}, {}, {}, 0))
    }
  }

  return (
    <div style={s.page}>
      <div style={s.ambient} />

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.headerTitle}>INSIGHTS</div>
      </div>

      <div style={s.main}>
        <div style={s.sectionLabel}>Life Areas</div>
        <div style={s.grid}>
          {Object.entries(AREA_META).map(([area, meta]) => {
            const scoreKey = `${area}_score`
            const val = scores?.[scoreKey] || 0
            const scoreColor = val >= 75 ? '#4caf7d' : val >= 50 ? '#c9a84c' : val > 0 ? '#e05c5c' : '#6b6860'
            return (
              <div key={area} style={{ ...s.card, background: `linear-gradient(145deg, ${meta.color}18 0%, #1a1a26 60%)`, border: `1px solid ${meta.color}30` }}
                onClick={() => onNavigate('area', area)}>
                <div style={{ ...s.cardTop, background: meta.color }} />
                <div style={s.cardIcon}>{meta.icon}</div>
                <div style={s.cardName}>{meta.label}</div>
                <div style={{ ...s.cardScore, color: scoreColor }}>{val}</div>
                <div style={s.barBg}>
                  <div style={{ ...s.barFill, width: `${val}%`, background: meta.color }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* BOTTOM NAV */}
      <div style={s.bottomNav}>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => onNavigate('dashboard')}>
          <span style={s.navLabel}>Score</span>
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => onNavigate('friends')}>
          <span style={s.navLabel}>Friends</span>
        </div>
        <div style={{ ...s.navItem, opacity: 1 }}>
          <span style={{ ...s.navLabel, color: '#c9a84c', fontWeight: '600' }}>Insights</span>
          <div style={s.navDot} />
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => onNavigate('coach')}>
          <span style={s.navLabel}>Coach</span>
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => onNavigate('profile')}>
          <span style={s.navLabel}>Settings</span>
        </div>
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.12) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '4px', color: '#c9a84c' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '28px 20px 120px', position: 'relative', zIndex: 1 },
  sectionLabel: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '16px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  card: { borderRadius: '16px', padding: '18px 14px', position: 'relative', overflow: 'hidden', cursor: 'pointer' },
  cardTop: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px' },
  cardIcon: { fontSize: '22px', marginBottom: '10px' },
  cardName: { fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  cardScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', lineHeight: 1, marginBottom: '10px' },
  barBg: { height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '2px', transition: 'width 0.8s ease' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(6,6,8,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingTop: '12px', paddingBottom: 'max(22px, env(safe-area-inset-bottom))', zIndex: 100 },
  navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  navLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860' },
  navDot: { width: '4px', height: '4px', borderRadius: '50%', background: '#c9a84c' },
}
